/**
 * <StatStrip items={[{ key, label, value, tone, hint, active, onClick }]} />
 *
 * The one way pages show their headline counts: a single compact row (about 56px tall) instead of big
 * tiles, so the list the user came to work on starts in the first screen. On phones the row scrolls
 * sideways instead of stacking into a column of cards.
 *
 *   tone    'red' | 'orange' | 'amber' | 'purple' | 'blue' | 'green' | 'gray' (a coloured dot + number)
 *   onClick optional: makes the stat a filter button; `active` shows it pressed (aria-pressed)
 *   hint    optional tooltip with the rule behind the number
 */
import React from 'react';

const TONES = {
  red: { dot: 'bg-red-500', num: 'text-red-700', on: 'border-red-300 bg-red-50' },
  orange: { dot: 'bg-orange-500', num: 'text-orange-700', on: 'border-orange-300 bg-orange-50' },
  amber: { dot: 'bg-amber-500', num: 'text-amber-800', on: 'border-amber-300 bg-amber-50' },
  purple: { dot: 'bg-purple-500', num: 'text-purple-700', on: 'border-purple-300 bg-purple-50' },
  blue: { dot: 'bg-blue-500', num: 'text-blue-700', on: 'border-blue-300 bg-blue-50' },
  green: { dot: 'bg-green-600', num: 'text-green-700', on: 'border-green-300 bg-green-50' },
  gray: { dot: 'bg-gray-400', num: 'text-gray-900', on: 'border-gray-400 bg-gray-50' },
};

const Stat = ({ label, value, tone = 'gray', hint, active, onClick, testId }) => {
  const t = TONES[tone] || TONES.gray;
  const quiet = value === 0 || value === '0';
  const body = (
    <>
      <span className={`h-2 w-2 shrink-0 rounded-full ${quiet ? 'bg-gray-300' : t.dot}`} aria-hidden="true" />
      <span className={`text-[18px] font-semibold leading-none tabular-nums ${quiet ? 'text-gray-400' : t.num}`}>{value ?? '–'}</span>
      <span className="whitespace-nowrap text-[12.5px] text-gray-600">{label}</span>
    </>
  );
  const base = 'flex h-11 shrink-0 items-center gap-2 rounded-lg border px-3 transition-colors';
  if (!onClick) {
    return <div className={`${base} border-gray-200 bg-white`} title={hint} data-testid={testId}>{body}</div>;
  }
  return (
    <button type="button" onClick={onClick} title={hint} aria-pressed={!!active} data-testid={testId}
      className={`${base} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${active ? t.on : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}>
      {body}
    </button>
  );
};

export const StatStrip = ({ items, className = '', testId = 'stat-strip' }) => (
  <div className={`-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible ${className}`} data-testid={testId} role="group" aria-label="Summary">
    {items.map(({ key, ...item }) => <Stat key={key} testId={`${testId}-${key}`} {...item} />)}
  </div>
);
